import { logError, logDebug } from './Logging';
import { getAccessToken, getOidcUserId, MOCK, userManager, signoutState } from '@auth/userManager';
import { mockApiRequest } from './MockApiService';
import { getConfig } from '@config/runtimeConfig';

const originalBaseApiRequest = async (
  signal,
  path,
  service = 'gpr',
  method = 'GET',
  body = undefined,
  headers = {},
  downloadFile = false
) => {
  let apiKey = getConfig('API_KEY');

  let uri = getConfig('GPR_API');
  if (service === 'logging') uri = getConfig('LOGGING_API');

  uri = `${uri}${path}`;

  // Entra oid (GUID) kept for client-side logging only. It is NOT sent to the backend: on the OIDC path the
  // server derives the user from the Bearer token (oxe:user_id, resolved by email), and user-scoped calls carry
  // the numeric id from /me in the request body. Sending the GUID here would mismatch the int user-id model.
  const userId = await getOidcUserId();

  const requestHeaders = {
    browserId: 1,
    sameSite: 'none',
    withCredentials: true,
    EsgianApiKey: apiKey,
    SupportsCredentials: true,
    'access-control-allow-origin': '*',
    'Access-Control-Allow-Headers':
      'X-Requested-With, X-HTTP-Method-Override, Content-Type, Accept',
    'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,UPDATE,OPTIONS',
    'Access-Control-Allow-Credentials': true,
    'sec-fetch-site': 'same-origin',
    'Content-type': 'application/json',
    'Keep-Alive': 'timeout=100, max=100',
    ...headers
  };

  if (service !== 'logging') {
    const token = await getAccessToken();
    if (token) {
      requestHeaders.Authorization ??= `Bearer ${token}`;
    } else if (!MOCK) {
      // No token. Do NOT start a re-auth while a logout is in flight:
      if (!signoutState.inProgress) {
        userManager.signinRedirect();
      }
      // Reject instead of resolving with undefined: a resolved undefined looks like a successful
      // empty response to every caller (createAsyncThunk lands in `fulfilled` with no payload,
      // and reducers then crash on payload.<field>).
      return Promise.reject({ missingToken: true, errors: ['Not authenticated'] });
    }
  } else {
    // loggingservice authenticates the API key as a Bearer token (it no longer accepts the custom
    // EsgianApiKey header). It reads only Authorization, LoggingHostName and Content-Type, so strip the
    // rest to keep the CORS preflight minimal. The dropped ones were never functional: CORS is decided by
    // the server's response, not by client request headers, and Access-Control-*/withCredentials/
    // SupportsCredentials/sameSite/sec-fetch-site/Keep-Alive are response headers, config options, or
    // browser-forbidden header names.
    requestHeaders.Authorization = `Bearer ${apiKey}`;
    [
      'browserId',
      'sameSite',
      'withCredentials',
      'EsgianApiKey',
      'SupportsCredentials',
      'access-control-allow-origin',
      'Access-Control-Allow-Headers',
      'Access-Control-Allow-Methods',
      'Access-Control-Allow-Credentials',
      'sec-fetch-site',
      'Keep-Alive'
    ].forEach((header) => delete requestHeaders[header]);
  }

  if (service !== 'logging')
    logDebug(`Executing ${method} ${path} in service ${service}.`, undefined, {
      method,
      path,
      userId
    });

  return await fetch(uri, {
    signal: signal,
    mode: 'cors',
    // loggingservice authenticates via the Authorization: Bearer header, not cookies, so omit credentials.
    // This lets its CORS respond with a wildcard origin and avoids the any-origin+credentials footgun.
    credentials: service === 'logging' ? 'omit' : 'include',
    method: method === 'download' ? 'POST' : method,
    body: JSON.stringify(body),
    headers: requestHeaders
  })
    .then(async (response) => {
      const reject = (errors) => {
        return Promise.reject({
          status: response.status,
          errors
        });
      };

      if (response.status > 299) {
        const rspBody = await response.text().then((output) => {
          if (!output) return '';
          if (isJson(output)) {
            const parsed = JSON.parse(output);
            if (typeof parsed === 'string') return parsed;
            if (parsed?.message) return parsed.message;
            if (parsed?.error) return parsed.error;
            if (parsed?.title) return parsed.title;
            return '';
          }
          return output;
        });

        switch (response.status) {
          case 400:
            if (rspBody.length !== 0) return reject([`Invalid request: ${rspBody}`]);
            return reject([`Invalid request`]);
          case 401:
            return reject(['Unauthorized']);
          case 403:
            return reject(['Forbidden']);
          case 404:
            if (rspBody.length !== 0) return reject([rspBody]);
            return reject(['Requested URL does not exist']);
          case 412:
            return reject(['No data was found in clarify']);
          case 500:
            if (rspBody.length !== 0) return reject([rspBody]);
            return reject(['Internal server error']);
          case 504:
            return reject(['Request timed out']);
        }
      }

      try {
        let data;
        data = await response;
        if (method === 'download' || downloadFile) {
          if (response.status === 400) {
            response.text().then((b) => {
              return reject([`Couldn't download the file: ${b}`]);
            });
            return reject(["Couldn't download the file"]);
          }
          const contentDisposition = response.headers.get('content-disposition');
          let fileName = '';
          if (contentDisposition) {
            const encodedFileNameMatch =
              /filename\*=([^']*)''([^;\s]+)/i.exec(contentDisposition);
            if (encodedFileNameMatch && encodedFileNameMatch[2]) {
              const encoding = (encodedFileNameMatch[1] || '').toLowerCase();
              const encodedFileName = encodedFileNameMatch[2];
              fileName =
                encoding === '' || encoding === 'utf-8'
                  ? decodeURIComponent(encodedFileName)
                  : encodedFileName;
            } else {
              const quotedMatch = /filename="([^"]*)"/.exec(contentDisposition);
              if (quotedMatch && quotedMatch[1]) {
                fileName = quotedMatch[1];
              } else {
                const unquotedMatch = /filename=([^;\s]+)/.exec(contentDisposition);
                if (unquotedMatch && unquotedMatch[1]) {
                  fileName = unquotedMatch[1];
                }
              }
            }
          }
          return response.blob().then((blob) => ({ blob, fileName }));
        }
        if (method === 'DELETE' || method === 'PUT') {
          return response;
        }
        let responseString = await response?.text();
        if (response.status === 202 && responseString === '') return {};
        if (!responseString) return null;
        data = JSON.parse(responseString);
        if (response.ok) {
          return data;
        }
        return reject(data[Object.keys(data)[0]]);
      } catch (e) {
        return reject(['Unknown error.']);
      }
    })
    .catch((err) => {
      if (service !== 'logging') {
        if (err.name === 'AbortError' && !err?.status) {
          var abortErr = serializeError(err);
          logError(abortErr, `${method} ${path} in service ${service} was aborted.`, {
            method,
            path,
            userId
          });
          return Promise.reject({ name: 'AbortError', errors: ['Request Aborted'] });
        } else {
          logError(err, `${method} ${path} in service ${service} failed with StatusCode: ${err?.status}.`, {
            method,
            path,
            userId,
            statusCode: err?.status
          });
        }
      }
      if (err?.errors?.length && typeof err?.errors[0] === 'string') {
        return Promise.reject({
          status: err.status ? err.status : undefined,
          errors: typeof err.errors === 'string' ? [err.errors] : err.errors
        });
      }
      return Promise.reject({ status: 500, errors: ['Network error'] });
    });
};

export const BaseApiRequest = async (
  signal,
  path,
  service = 'gpr',
  method = 'GET',
  body = undefined,
  headers = {},
  downloadFile = false
) => {
  if (method !== 'GET' || service !== 'gpr') {
    return originalBaseApiRequest(signal, path, service, method, body, headers, downloadFile);
  }
  return mockApiRequest(
    signal,
    path,
    service,
    method,
    body,
    headers,
    downloadFile,
    originalBaseApiRequest
  );
};

function isJson(str) {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}

function serializeError(err) {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }
  return err;
}
